import { useState } from "react";
import { api, formatErr } from "@/lib/api";
import { toast } from "sonner";
import { CircleNotch, Lightning, Warning } from "@phosphor-icons/react";

const msg = (e) => formatErr(e?.response?.data?.detail) || e.message;

/** Shared admin payout control: Disburse → Paystack transfer, OTP finalize, retry after failure. */
export default function PayoutControl({ payout, amountLabel, disbursePath, otpPath, onDone, testid, retryNote = "The loan is still approved, so you can retry." }) {
  const [busy, setBusy] = useState(false);
  const [confirm, setConfirm] = useState(false);
  const [otp, setOtp] = useState("");
  const [otpNeeded, setOtpNeeded] = useState(payout?.status === "otp");
  const [lastError, setLastError] = useState(payout?.status === "failed" || payout?.status === "reversed" ? payout?.error : "");

  const run = async (fn) => {
    setBusy(true);
    try { await fn(); } catch (e) { const m = msg(e); setLastError(m); toast.error(m); onDone?.(); } finally { setBusy(false); setConfirm(false); }
  };

  const disburse = () => run(async () => {
    const { data } = await api.post(disbursePath);
    if (data.otp_required) {
      setOtpNeeded(true);
      toast.info("Paystack sent an OTP to the business owner's phone. Enter it to finish the transfer.");
      return;
    }
    setLastError("");
    toast.success(`Transfer started on Paystack (${data.transfer_code || "no code"})`);
    onDone?.();
  });

  const otpAction = (action) => run(async () => {
    await api.post(`${otpPath}-action`, { action });
    if (action === "cancel") { setOtpNeeded(false); setOtp(""); toast.info("Transfer cancelled. You can start a new payout."); onDone?.(); }
    else toast.success("Paystack sent a new OTP");
  });

  const finalize = () => run(async () => {
    const { data } = await api.post(otpPath, { otp });
    setLastError(""); setOtpNeeded(false);
    toast.success(`Transfer confirmed (${data.transfer_code || "no code"})`);
    onDone?.();
  });

  return (
    <div className="space-y-2" data-testid={testid}>
      {lastError && (
        <div className="flex items-start gap-1.5 text-xs text-[#B91C1C] bg-[#FDECEC] rounded-lg p-2" data-testid={`${testid}-error`}>
          <Warning size={14} weight="fill" className="mt-0.5 shrink-0" /> <span>Last attempt failed: {String(lastError).replace(/\.+$/, "")}. {retryNote}</span>
        </div>
      )}
      {otpNeeded ? (
        <div className="flex gap-2">
          <input className="input !py-2 text-xs font-mono" placeholder="Paystack OTP" maxLength={8} value={otp}
            onChange={(e) => setOtp(e.target.value.replace(/\D/g, ""))} data-testid={`${testid}-otp-input`} />
          <button type="button" onClick={finalize} disabled={busy || otp.length < 4} className="btn-primary !py-2 text-xs shrink-0" data-testid={`${testid}-otp-submit`}>
            {busy ? <CircleNotch className="animate-spin" size={14} /> : "Finish transfer"}
          </button>
          <button type="button" onClick={() => otpAction("resend")} disabled={busy} className="btn-ghost !py-2 text-xs shrink-0" data-testid={`${testid}-otp-resend`}>Resend</button>
          <button type="button" onClick={() => otpAction("cancel")} disabled={busy} className="btn-ghost !py-2 text-xs shrink-0" data-testid={`${testid}-otp-cancel`}>Cancel</button>
        </div>
      ) : !confirm ? (
        <button type="button" onClick={() => setConfirm(true)} className="btn-primary !py-2 text-xs w-full" data-testid={`${testid}-button`}>
          <Lightning size={14} weight="fill" /> {lastError ? "Retry payout" : "Disburse"} {amountLabel}
        </button>
      ) : (
        <div className="flex gap-2">
          <button type="button" onClick={disburse} disabled={busy} className="btn-primary !py-2 text-xs flex-1" data-testid={`${testid}-confirm`}>
            {busy ? <CircleNotch className="animate-spin" size={14} /> : "Confirm and send via Paystack"}
          </button>
          <button type="button" onClick={() => setConfirm(false)} disabled={busy} className="btn-ghost !py-2 text-xs">Back</button>
        </div>
      )}
    </div>
  );
}
